var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ConnectMessage, BusSubscriptionsReadyMessage, BusPortMessage, RequestBusSubscriptionsReadyMessage, SubscribeMessage, GatherSystemStatsMessage, ModuleStatsMessage, HandshakeMessage, WorkerDiedMessage, SystemInitMessage } from "./BusMessages";
import { wrapMessage, unwrapMessage, MESSAGE_SEPERATOR, SUBJECT_TYPE_SEPERATOR, DebugMessage, ANSWER_MESSAGE_SEPERATOR, AskMessage, METADATA_SEPERATOR, isPromise, AskErrorMessage, getRegExpForSubscriptionPattern, MessageFactory, ByteStreamReader } from "flavr-common";
import { ENVIRONMENT_IS_WORKER } from "../../messages/environment";
import { now } from "../../messages/timing/now";
import { RingBuffer } from "./ringBuffer/RingBuffer";
import { BusConnectionImpl } from "./BusConnectionImpl";
import { BusServiceBase, ENABLE_PROCESS_MESSAGE_STATS } from "./BusServiceBase";
import { SharedWorkerPolyfill, BusWorkerReceiverMessagePortPolyfill } from "./polyfill/SharedWorkerPolyfill";
import { OffscreenMessage } from "../../messages/offscreen/OffscreenMessages";
import { SharedArrayBufferMessage } from "../../messages/sharedArray/SharedArrayMessages";
import { MainMessagePort } from "./MainMessagePort";
/**
 * This Service is a facade for the shared message bus.
 * Workers should use connect (providing a port they got from BusServiceMain) and define their receive callback and send messages with send.
 */
class BusService extends BusServiceBase {
    constructor() {
        super();
        /**
         * message callbacks in the same worker (only public for BusConnectionImpl!)
         */
        this.localSubscriptions = new Map();
        /**
         * This map is used for caching the subscriptions that handle a specific MessageType,
         * so we don't have to test which pattern of the subscriptions matches the MessageType
         * every time we receive/send a message.
         */
        this.messageTypeToLocalSubscriptions = new Map();
        this.gatherSystemStats = new Map();
        this.pendingTransferables = [];
        this.localTransferSubscriptions = new Map();
        /**
         * by default, we process the message as we receive them (main overwrites this)
         */
        this.handleMessage = (message, metaData, subscriptions) => {
            if (Array.isArray(message)) {
                for (const m of message) {
                    this.processMessage({
                        message: m,
                        metaData,
                        subscriptions
                    });
                }
            }
            else {
                this.processMessage({
                    message,
                    metaData,
                    subscriptions
                });
            }
        };
        this.receiveMessage = (evt) => {
            try {
                if (typeof evt.data !== "string") {
                    if (evt.data.isTransfer) {
                        this.handleTransferMessage(evt.data);
                        return;
                    }
                    if (evt.data.meta) {
                        if (evt.data.meta.offscreen) {
                            this.handleOffscreenMessage(evt.data);
                            return;
                        }
                    }
                    if (evt.data.sharedArrayBuffer) {
                        this.handleSharedArray(evt.data);
                        return;
                    }
                    this.handleBuffer(evt);
                    return;
                }
                this.handleStringMessage(evt.data);
            }
            catch (e) {
                const msg = '[bus] An error occurred while receiving a message\n';
                console.error(msg, evt, e);
            }
        };
        const handler = this.addSubscription("busService", [SystemInitMessage.typestatic], () => {
            this.initReceived = true;
            this.removeSubscription(handler);
            for (const data of this.pendingTransferables) {
                this.transfer(data.id, data.targetWorkerId, data.pattern, data.objData, data.transfer);
            }
            this.pendingTransferables = [];
        });
    }
    addSubscription(moduleId, pattern, callback) {
        const handler = this.getNextSubscriptionHandler();
        const extendedPattern = pattern.map(getRegExpForSubscriptionPattern);
        this.localSubscriptions.set(handler, {
            moduleId,
            pattern,
            extendedPattern,
            callback
        });
        // the subscriptions have changed so we have to update the messageTypeToLocalSubscriptions
        this.messageTypeToLocalSubscriptions.clear();
        return handler;
    }
    removeSubscription(handler) {
        const subscription = this.localSubscriptions.get(handler);
        if (subscription !== undefined) {
            // unsubscribe local
            this.localSubscriptions.delete(handler);
        }
        // the subscriptions have changed so we have to update the messageTypeToLocalSubscriptions
        this.messageTypeToLocalSubscriptions.clear();
        return subscription;
    }
    addTransferSubscription(pattern, callback) {
        let subscriptions = this.localTransferSubscriptions.get(pattern);
        if (subscriptions === undefined) {
            subscriptions = new Set();
            this.localTransferSubscriptions.set(pattern, subscriptions);
        }
        subscriptions.add(callback);
    }
    removeTransferSubscription(pattern, callback) {
        const subscriptions = this.localTransferSubscriptions.get(pattern);
        if (subscriptions === undefined)
            return;
        subscriptions.delete(callback);
        if (subscriptions.size === 0) {
            this.localTransferSubscriptions.delete(pattern);
        }
    }
    getNumSubscriptions() {
        return this.localSubscriptions.size;
    }
    generateAskId() {
        return `${this.getWorkerId()}${BusService.lastAskId++}`;
    }
    createAskMessage(message, callback) {
        const askId = this.generateAskId();
        this.askCallbacks.set(askId, { callback });
        return new AskMessage(askId, message);
    }
    registerDedicatedWorkerAskCallback(askId, callback) {
        this.askCallbacks.set(askId, { dedicatedWorkerCallback: callback });
    }
    /**
     * store the port to the Bus (SharedWebWorker) and provide workerId to be set in every outgoing message in message.source
     */
    connectBusPort(port, workerId, appUid) {
        return __awaiter(this, void 0, void 0, function* () {
            this.workerId = workerId;
            this.port = port;
            this.port.onmessageerror = (evt) => {
                console.error("[bus] Bus received error from worker: ", evt);
            };
            this.port.onmessage = this.receiveMessage;
            this.port.start();
            // connect with the bus, giving him our workerId
            const connect = new ConnectMessage(appUid);
            this.port.postMessage(wrapMessage(connect, this.workerId, "connect", Date.now()));
            // send any subscription-patterns already gathered from submodules to bus now
            if (this.localSubscriptions.size > 0) {
                const timeStamp = Date.now();
                // ToDo: post one message
                for (const subscription of this.localSubscriptions.values()) {
                    this.port.postMessage(wrapMessage(new SubscribeMessage(subscription.pattern), this.workerId, "connect", timeStamp));
                }
            }
            yield this.waitForInitialSubscriptionsReady();
        });
    }
    waitForInitialSubscriptionsReady() {
        if (this.port === undefined) {
            throw new Error("We expect this.port to be defined here.");
        }
        if (this.pendingBusSubscriptionsReady !== undefined) {
            return Promise.reject(new Error(`[bus] Already waiting for initial subscriptions ready for worker "${this.workerId}"`));
        }
        return new Promise((resolve, reject) => {
            var _a;
            const timeoutHandle = self.setTimeout(() => {
                var _a;
                if (((_a = this.pendingBusSubscriptionsReady) === null || _a === void 0 ? void 0 : _a.workerId) === this.workerId) {
                    this.pendingBusSubscriptionsReady = undefined;
                }
                reject(new Error(`[bus] Waiting for initial subscriptions ready of worker "${this.workerId}" timed out`));
            }, 5000);
            this.pendingBusSubscriptionsReady = {
                workerId: this.workerId,
                resolve: () => {
                    self.clearTimeout(timeoutHandle);
                    this.pendingBusSubscriptionsReady = undefined;
                    resolve();
                },
                timeoutHandle
            };
            (_a = this.port) === null || _a === void 0 ? void 0 : _a.postMessage(wrapMessage(new RequestBusSubscriptionsReadyMessage(this.workerId), this.workerId, "connect", Date.now()));
        });
    }
    portConnected() {
        return this.port !== undefined;
    }
    getNextSubscriptionHandler() {
        return BusService.lastHandler++;
    }
    processMessage({ message, metaData, subscriptions }) {
        if (AskMessage.is(message) && metaData.source.workerId !== this.getWorkerId()) {
            this.askWorkerIds.set(message.askId, metaData.source.workerId);
        }
        for (const subscription of subscriptions) {
            const time = ENABLE_PROCESS_MESSAGE_STATS ? now() : 0;
            const result = subscription.callback(message, metaData);
            if (isPromise(result)) {
                Promise.resolve(result).catch((error) => {
                    if (Array.isArray(error)) {
                        for (const ele of error) {
                            if (AskErrorMessage.is(ele)) {
                                console.error("[bus] processMessage", message.type, ele.error, metaData);
                                continue;
                            }
                            console.error("[bus] processMessage", message.type, error, metaData);
                        }
                        return;
                    }
                    console.error("[bus] processMessage", message.type, error, metaData);
                });
            }
            if (ENABLE_PROCESS_MESSAGE_STATS) {
                const elapsedTime = now() - time;
                this.processMessageStats(subscription.moduleId, message, metaData, elapsedTime);
            }
        }
    }
    reactToGatherMessage(isLocal) {
        if (!this.workerId) {
            throw new Error("We expect this.workerId to be defined here.");
        }
        if (this.port === undefined) {
            throw new Error("We expect this.port to be defined here.");
        }
        for (const [moduleId, moduleStats] of this.gatherSystemStats) {
            const statsInfos = [];
            for (const state of moduleStats.values()) {
                statsInfos.push(state);
            }
            // create and send the ModuleStatsMessage:
            const systemStats = new ModuleStatsMessage(this.workerId, moduleId, statsInfos);
            const pattern = `${systemStats.subject}${SUBJECT_TYPE_SEPERATOR}${systemStats.type}`;
            if (isLocal) {
                this.connection(moduleId).sendLocal(systemStats, Date.now(), pattern);
            }
            else {
                this.connection(moduleId).sendBuffer(systemStats, Date.now(), pattern);
            }
            // reset data:
            for (const sourceModule of moduleStats.values()) {
                for (const messageType in sourceModule.statsByMessageType) {
                    const message = sourceModule.statsByMessageType[messageType];
                    message.countDelta = 0;
                    message.processingTimeDelta = 0;
                }
            }
        }
    }
    processMessageStats(moduleId, message, metaData, processingTime) {
        if (this.port === undefined) {
            throw new Error("We expect this.port to be defined here.");
        }
        const sourceModuleId = metaData.source.moduleId;
        const sourceWorkerId = metaData.source.workerId;
        const messageType = message.type;
        if (ModuleStatsMessage.is(message) || GatherSystemStatsMessage.is(message)) {
            return;
        }
        let stats = this.gatherSystemStats.get(moduleId);
        if (!stats) {
            this.gatherSystemStats.set(moduleId, (stats = new Map()));
        }
        let source = stats.get(sourceModuleId);
        if (!source) {
            source = { sourceModuleId, sourceWorkerId, statsByMessageType: {} };
            stats.set(sourceModuleId, source);
        }
        let msgData = source.statsByMessageType[messageType];
        if (!msgData) {
            msgData = source.statsByMessageType[messageType] = {
                countDelta: 0,
                countTotal: 0,
                processingTimeDelta: 0,
                processingTimeTotal: 0
            };
        }
        msgData.countDelta++;
        msgData.countTotal++;
        msgData.processingTimeDelta += processingTime;
        msgData.processingTimeTotal += processingTime;
    }
    postBuffer(pattern, moduleId, timeStamp, buf, askId, targetWorkerId, clientId, transport) {
        if (this.port === undefined) {
            throw new Error("We expect this.port to be defined here.");
        }
        this.port.postMessage({
            pattern,
            moduleId,
            workerId: this.workerId,
            timeStamp,
            askId,
            targetWorkerId,
            clientId,
            transport,
            buf
        }, [buf]);
    }
    // for BusConnection only to avoid having to make this.port public
    postWrappedMessage(wrappedMessage) {
        if (this.port === undefined) {
            throw new Error("We expect this.port to be defined here.");
        }
        this.port.postMessage(wrappedMessage);
    }
    postSharedArrayBuffer(moduleId, targetWorkerId, id, sharedArrayBuffer) {
        if (!this.port)
            throw new Error(`"We expect this.port to be defined here.`);
        const pattern = `sharedArrayBuffer/${id}`;
        const timeStamp = Date.now();
        const askId = undefined;
        const clientId = undefined;
        this.port.postMessage({
            pattern,
            moduleId,
            timeStamp,
            askId,
            targetWorkerId,
            clientId,
            id,
            sharedArrayBuffer
        });
    }
    transfer(id, targetWorkerId, pattern, objData, transfer) {
        if (!this.port) {
            throw new Error("We expect this.port to be defined here.");
        }
        if (targetWorkerId === this.getWorkerId()) {
            throw new Error(`Can't send to own worker ${targetWorkerId}`);
        }
        const timeStamp = Date.now();
        if (!this.isReady()) {
            this.pendingTransferables.push({
                id,
                targetWorkerId,
                pattern,
                timeStamp,
                objData,
                transfer
            });
            return;
        }
        const msg = {
            isTransfer: true,
            id,
            pattern,
            targetWorkerId,
            timeStamp,
            objData
        };
        this.port.postMessage(msg, transfer);
    }
    handleOffscreenMessage(data) {
        const subscriptions = this.getSubscribersForMessageType(`${OffscreenMessage.subjectstatic}${SUBJECT_TYPE_SEPERATOR}${OffscreenMessage.typestatic}`);
        if (subscriptions.length === 0)
            return;
        const message = new OffscreenMessage(data.meta.id, data.meta.rendererType, data.meta.offscreen, data.meta.width, data.meta.height, data.meta.pixelRatio);
        this.processMessage({
            message,
            metaData: {
                source: data.source,
                timeStamp: data.timeStamp,
                clientId: undefined,
                transport: "normal"
            },
            subscriptions
        });
    }
    handleTransferMessage(data) {
        const pattern = data.pattern;
        const subscriptions = this.localTransferSubscriptions.get(pattern);
        if (subscriptions === undefined)
            return;
        for (const subscriptionTransferCallback of subscriptions) {
            subscriptionTransferCallback({
                id: data.id,
                timestamp: data.timeStamp,
                data: data.objData
            });
        }
    }
    handleSharedArray(data) {
        const subscriptions = this.getSubscribersForMessageType(`${SharedArrayBufferMessage.subjectstatic}${SUBJECT_TYPE_SEPERATOR}${SharedArrayBufferMessage.typestatic}`);
        if (subscriptions.length === 0)
            return;
        const message = new SharedArrayBufferMessage(data.id, data.sharedArrayBuffer);
        this.processMessage({
            message,
            metaData: {
                source: data.source,
                timeStamp: data.timeStamp,
                clientId: undefined,
                transport: "normal"
            },
            subscriptions
        });
    }
    handleStringMessage(msgString) {
        // filter message by subject/type regexp
        const colon = msgString.indexOf(MESSAGE_SEPERATOR);
        if (colon === -1) {
            throw new Error("Invalid Message, missing MESSAGE_SEPERATOR. have you used busService.send() ?");
        }
        const subStr = msgString.substring(0, colon);
        const answerPos = subStr.indexOf(ANSWER_MESSAGE_SEPERATOR);
        let msgInfos;
        if (answerPos !== -1) {
            const colon2 = msgString.indexOf(MESSAGE_SEPERATOR, colon + 1);
            if (colon2 === -1) {
                throw new Error("Invalid Message, missing MESSAGE_SEPERATOR. have you used busService.send() ?");
            }
            msgInfos = msgString.substring(colon + 1, colon2).split(METADATA_SEPERATOR);
        }
        else {
            msgInfos = subStr.split(METADATA_SEPERATOR);
        }
        if (msgInfos.length === 0) {
            throw new Error("Invalid Message, missing METADATA_SEPERATOR. have you used busService.send() ?");
        }
        const metaData = {
            source: {
                workerId: msgInfos[1],
                moduleId: msgInfos[2]
            },
            timeStamp: Number(msgInfos[3]),
            transport: "normal"
        };
        if (answerPos !== -1) {
            const askId = subStr.substring(answerPos + 1);
            const callbackInfo = this.askCallbacks.get(askId);
            if (callbackInfo && !("dedicatedWorkerCallback" in callbackInfo)) {
                const data = msgString.substring(colon + 1);
                const parsedMessage = unwrapMessage(data);
                if (Array.isArray(parsedMessage)) {
                    callbackInfo.callback(parsedMessage, metaData);
                }
                else {
                    console.error("[bus] The messages in the answer message must be an array");
                }
                this.askCallbacks.delete(askId);
            }
            else {
                console.error(`[bus] No callback stored for askId ${askId}`);
            }
        }
        else {
            const subjectSlashType = msgInfos[0];
            if (subjectSlashType === `system${SUBJECT_TYPE_SEPERATOR}SystemInitMessage`) {
                this.initReceived = true;
                console.log(`[bus] ${this.workerId} got SystemInit, forwarding to ${this.localSubscriptions.size} subscribers`);
            }
            const parsedMessage = unwrapMessage(msgString);
            if (!Array.isArray(parsedMessage) && BusSubscriptionsReadyMessage.is(parsedMessage)) {
                if (parsedMessage.workerId === this.workerId && this.pendingBusSubscriptionsReady !== undefined) {
                    this.pendingBusSubscriptionsReady.resolve();
                    return;
                }
            }
            const subscribers = this.getSubscribersForMessageType(subjectSlashType);
            if (subscribers.length > 0) {
                this.handleMessage(parsedMessage, metaData, subscribers);
            }
            else {
                console.error(`[bus] Received a message, but no handler matched it! Message: ${msgString}`);
            }
        }
    }
    handleBuffer(evt) {
        var _a;
        const buffer = evt.data.buf;
        const workerId = evt.data.workerId;
        const moduleId = evt.data.moduleId;
        const timeStamp = evt.data.timeStamp;
        const askId = evt.data.askId;
        const clientId = evt.data.clientId;
        const pattern = evt.data.pattern;
        const targetWorkerId = evt.data.targetWorkerId;
        const metaData = {
            source: {
                workerId,
                moduleId
            },
            timeStamp,
            clientId,
            transport: (_a = evt.data.transport) !== null && _a !== void 0 ? _a : "normal"
        };
        if (targetWorkerId !== undefined && askId !== undefined) {
            // Answer Message
            this.handleAnswerBuffer(buffer, evt.data, metaData, askId);
            return;
        }
        const subscribers = this.getSubscribersForMessageType(pattern);
        if (subscribers.length > 0) {
            const stream = new ByteStreamReader(buffer);
            const length = stream.getUint32();
            if (askId !== undefined) {
                this.handleAskBuffer(stream, metaData, askId, subscribers);
            }
            else {
                this.handleBufferMessage(stream, length, metaData, subscribers);
            }
            if (evt.fromDedicatedWorker) {
                if (this.port) {
                    this.port.postMessage({
                        pattern,
                        moduleId,
                        workerId,
                        timeStamp,
                        askId,
                        targetWorkerId,
                        clientId,
                        buf: buffer
                    }, [buffer]);
                }
            }
        }
        else if (evt.fromDedicatedWorker) {
            // send direct to shared worker
            if (this.port) {
                this.port.postMessage({
                    pattern,
                    moduleId,
                    workerId,
                    timeStamp,
                    askId,
                    targetWorkerId,
                    clientId,
                    buf: buffer
                }, [buffer]);
            }
        }
        else {
            console.error(`[bus] Received message, but no handler matched it! message: `, evt);
        }
    }
    handleAnswerBuffer(buffer, data, metaData, askId) {
        const callbackInfo = this.askCallbacks.get(askId);
        if (!callbackInfo) {
            console.error(`[bus] Can't find callback for askId ${askId}`);
            return;
        }
        this.askCallbacks.delete(askId);
        if ("dedicatedWorkerCallback" in callbackInfo) {
            callbackInfo.dedicatedWorkerCallback(data);
            return;
        }
        const stream = new ByteStreamReader(buffer);
        const msgLength = stream.getUint32();
        const msgs = [];
        for (let i = 0; i < msgLength; ++i) {
            const typeHash = stream.getTypeHash();
            const msg = MessageFactory.create(typeHash);
            if (msg.serialization) {
                msg.serialization(stream);
            }
            msgs.push(msg);
        }
        callbackInfo.callback(msgs, metaData);
    }
    handleAskBuffer(stream, metaData, askId, subscribers) {
        const typeHash = stream.getTypeHash();
        const msg = MessageFactory.create(typeHash);
        if (msg.serialization) {
            msg.serialization(stream);
        }
        this.handleMessage(new AskMessage(askId, msg), metaData, subscribers);
    }
    handleBufferMessage(stream, msgLength, metaData, subscribers) {
        const typeHash = stream.getTypeHash();
        const creator = MessageFactory.getCreator(typeHash);
        for (let i = 0; i < msgLength; ++i) {
            const msg = MessageFactory.new(creator);
            if (msg.serialization) {
                msg.serialization(stream);
            }
            this.handleMessage(msg, metaData, subscribers);
        }
    }
    getSubscribersForMessageType(subjectSlashType) {
        // test if we already know wich Subscriptions are handling the message of type 'subjectSlashType'.
        let subscribers = this.messageTypeToLocalSubscriptions.get(subjectSlashType);
        if (subscribers) {
            return subscribers;
        }
        // This is the first time we deal with a message of type 'subjectSlashType'
        // or the subscriptins have changed sice we last dealt with a message of this type.
        // We have to test if 'subjectSlashType' matches any pattern of any subscription.
        subscribers = [];
        // send the message to all matching registered handlers
        for (const subscription of this.localSubscriptions.values()) {
            for (const regex of subscription.extendedPattern) {
                if (regex.test(subjectSlashType)) {
                    subscribers.push(subscription);
                    break;
                }
            }
        }
        // store the list of subscriptions so we don't have to test the 'subjectSlashType' against
        // all subscription-pattern, the next time we handle a message to this type.
        this.messageTypeToLocalSubscriptions.set(subjectSlashType, subscribers);
        return subscribers;
    }
    connection(moduleId) {
        if (!moduleId) {
            throw new Error("Modulid for the connection must not be empty");
        }
        let connection = this.connections.get(moduleId);
        if (!connection) {
            connection = new BusConnectionImpl(this, moduleId);
            this.connections.set(moduleId, connection);
        }
        return connection;
    }
}
/*
 * keep track of subscription handlers;
 */
BusService.lastHandler = 0;
BusService.lastAskId = 0;
/**
 * The Main Thread will have the additional Options to
 * 1. create the bus (createSharedWorker) and
 * 2. spawn ports for Workers (spawnBusPortForWorker)
 */
class BusServiceMain extends BusService {
    constructor() {
        super(...arguments);
        this.useSharedWorkerPolyfill = false;
        /**
         * to track if the bus has already been created
         */
        this.createdInMain = false;
        /**
         * we throttle the amount of messages to process the remaining budget per frame
         * after rendering we have only so long to handle messages from the bus, before we must render again.
         */
        this.messageProcessingUntil = 0;
        /**
         * incoming bessages get stored here first
         */
        this.messageBuffer = new RingBuffer(1000);
        this.messageCounter = 0;
        this.isWindowHidden = false;
        /**
         * overwrite behavior: in main we just store the messages as we receive them, so we can sync their processing with rendering
         */
        this.handleMessage = (message, metaData, subscriptions) => {
            // when the main tab is hidden, process the messages immediately
            if (this.isWindowHidden) {
                while (this.processBufferedMessages(0))
                    ;
                if (Array.isArray(message)) {
                    for (const msg of message) {
                        super.processMessage({
                            message: msg,
                            metaData,
                            subscriptions
                        });
                    }
                }
                else {
                    super.processMessage({
                        message,
                        metaData,
                        subscriptions
                    });
                }
                return;
            }
            // when the buffer is full, process the messages immediately
            const free = this.messageBuffer.getFreeBufferLength();
            if (free <= (Array.isArray(message) ? message.length : 0)) {
                this.processBufferedMessages(0);
                if (Array.isArray(message)) {
                    for (const msg of message) {
                        super.processMessage({
                            message: msg,
                            metaData,
                            subscriptions
                        });
                    }
                }
                else {
                    super.processMessage({
                        message,
                        metaData,
                        subscriptions
                    });
                }
                return;
            }
            this.processBufferedMessages(0);
            if (Array.isArray(message)) {
                for (const msg of message) {
                    super.processMessage({
                        message: msg,
                        metaData,
                        subscriptions
                    });
                }
            }
            else {
                super.processMessage({
                    message,
                    metaData,
                    subscriptions
                });
            }
        };
    }
    onWindowVisibleChanged(visible) {
        this.isWindowHidden = !visible;
    }
    setUseSharedWorkerPolyfill(userPolyfill) {
        this.useSharedWorkerPolyfill = userPolyfill;
    }
    isSharedWorkerSupported() {
        return !this.useSharedWorkerPolyfill && typeof SharedWorker !== "undefined";
    }
    waitForHandshake(bus, appUid) {
        return __awaiter(this, void 0, void 0, function* () {
            yield new Promise((resolve, reject) => {
                bus.onerror = (e) => {
                    bus.port.onmessage = null;
                    bus.onerror = null;
                    reject(e);
                };
                bus.port.onmessage = () => {
                    bus.port.onmessage = null;
                    bus.onerror = null;
                    resolve();
                };
                bus.port.postMessage(wrapMessage(new HandshakeMessage(appUid), this.workerId, "handshake", Date.now()));
            });
        });
    }
    spawnBusConnection(appUid) {
        return __awaiter(this, void 0, void 0, function* () {
            let bus;
            if (this.isSharedWorkerSupported()) {
                if (this.sharedBusWorker === undefined || !(this.sharedBusWorker instanceof SharedWorkerPolyfill)) {
                    // we need a new reference to port
                    bus = new SharedWorker("bus.bundle.js", "bus");
                }
                else {
                    return this.sharedBusWorker;
                }
                if (this.sharedBusWorker === undefined && appUid !== undefined) {
                    try {
                        yield this.waitForHandshake(bus, appUid)
                            .then(() => { })
                            .catch((reason) => {
                            console.error("[bus] Handshake failed: " + appUid, reason);
                        });
                    }
                    catch (_a) {
                        bus = new SharedWorkerPolyfill();
                    }
                }
            }
            else if (this.sharedBusWorker !== undefined) {
                return this.sharedBusWorker;
            }
            else {
                console.warn("[bus] Shared worker not supported");
                bus = new SharedWorkerPolyfill();
            }
            bus.onerror = (e) => {
                // if that happens we have a huge Problem!
                throw new Error(`Bus Worker died! ${JSON.stringify(e)}`);
            };
            bus.port.start();
            return bus;
        });
    }
    /**
     * create the shared worker from main
     */
    createSharedWorker(appUid) {
        return __awaiter(this, void 0, void 0, function* () {
            if (ENVIRONMENT_IS_WORKER) {
                throw new Error("Cannot create SharedWorker from another Worker. Create additional Ports with spawnBusConnection, transfer to worker and use connectBusPort from there instead.");
            }
            if (this.createdInMain) {
                throw new Error("Cannot create more than one instance of the sharedWorker in Main Thread!");
            }
            this.createdInMain = true;
            this.sharedBusWorker = yield this.spawnBusConnection(appUid);
            const mainPort = new MainMessagePort(this.sharedBusWorker.port);
            yield this.connectBusPort(mainPort, "main", appUid);
            const connection = this.connection("BusServiceMain");
            connection.subscribe(DebugMessage.typestatic, function (message, meta) {
                if (DebugMessage.is(message)) {
                    if (message.severity !== undefined) {
                        switch (message.severity) {
                            case "error":
                                console.error(`[server] Received error message`, message.message, meta.source);
                                break;
                            case "warn":
                                console.warn(`[server] Received warning message`, message.message, meta.source);
                                break;
                            case "info":
                                console.info(`[server] Received info message`, message.message, meta.source);
                                break;
                            default:
                                console.log(`[server] Received debug message`, message.message, meta.source);
                                break;
                        }
                    }
                    else {
                        // case no severity set
                        console.debug(`[server] Received debug message`, message.message, meta.source);
                    }
                }
                else {
                    // case no Debug Message
                    console.error(`[bus] BusServiceMain received unexpected message: ${JSON.stringify(message)}`);
                }
            });
            return this;
        });
    }
    /**
     * create a new connection to the SharedWebWorker and give it to a given Worker.
     */
    registerWorker(worker, appUid) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(this.sharedBusWorker instanceof SharedWorkerPolyfill)) {
                const bus = yield this.spawnBusConnection();
                worker.postMessage(new BusPortMessage(appUid, bus.port), [bus.port]);
                return;
            }
            if (this.port instanceof MainMessagePort) {
                if (this.port.port instanceof BusWorkerReceiverMessagePortPolyfill) {
                    this.port.port.worker.register(worker);
                    worker.postMessage(new BusPortMessage(appUid));
                }
                return;
            }
            if (this.port instanceof BusWorkerReceiverMessagePortPolyfill) {
                this.port.worker.register(worker);
                worker.postMessage(new BusPortMessage(appUid));
                return;
            }
            throw new Error(`Can't register worker, port isn't supported`);
        });
    }
    registerDedicatedWorker(dedicatedWorker, appUid) {
        ;
        this.port.addDedicatedPort(dedicatedWorker);
        dedicatedWorker.port.postMessage(new BusPortMessage(appUid));
    }
    unregisterDedicatedWorker(dedicatedWorker) {
        ;
        this.port.removeDedicatedPort(dedicatedWorker);
    }
    processBufferedMessages(until) {
        if (this.messageBuffer.getBufferLength() === 0) {
            return false;
        }
        while (until === 0 || until > now()) {
            for (let i = 0; i < 10; ++i) {
                const current = this.messageBuffer.pop();
                if (!current)
                    return false;
                super.processMessage(current);
            }
        }
        return true;
    }
    workerDied(workerId) {
        busServiceMain.postWrappedMessage(wrapMessage(new WorkerDiedMessage(workerId), this.workerId, "workerdied", Date.now()));
    }
    transferOffscreen(moduleId, id, rendererType, offscreen, width, height, pixelRatio) {
        if (this.port === undefined) {
            console.error("[bus] transferOffscreen: port not set");
            return;
        }
        const pattern = "offscreen/network";
        const askId = undefined;
        const timeStamp = Date.now();
        const clientId = undefined;
        const targetWorkerId = "physics"; // TODO: This might not work anymore as the physics worker doesn't exist anymore
        console.log('[bus] Transfer offscreen canvas to physics worker');
        this.port.postMessage({
            pattern,
            moduleId,
            timeStamp,
            askId,
            targetWorkerId,
            clientId,
            id,
            rendererType,
            offscreen,
            width,
            height,
            pixelRatio
        }, [offscreen]);
    }
}
/**
 * for direct import in main
 */
export const busServiceMain = new BusServiceMain();
/**
 * This Service is a Singleton, meaning there is one instance per Thread (Worker).
 */
export const busService = ENVIRONMENT_IS_WORKER ? new BusService() : busServiceMain;
